/**
 * World coordinate system (shared by client and server):
 *  - metres, right-handed, Y is up, gameplay happens on the XZ plane;
 *  - yaw 0 faces +Z, positive yaw turns towards +X (yaw = atan2(dx, dz)),
 *    which matches Three.js `object.rotation.y` for models authored facing +Z.
 */
export interface Vec2 {
  x: number;
  z: number;
}

export const TAU = Math.PI * 2;

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function dist2(ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax;
  const dz = bz - az;
  return dx * dx + dz * dz;
}

export function dist(ax: number, az: number, bx: number, bz: number): number {
  return Math.sqrt(dist2(ax, az, bx, bz));
}

/** Returns the unit vector of (x, z), or (0, 0) for a zero-length input. */
export function normalize(x: number, z: number): Vec2 {
  const l = Math.sqrt(x * x + z * z);
  if (l < 1e-9) return { x: 0, z: 0 };
  return { x: x / l, z: z / l };
}

export function yawFromDir(dx: number, dz: number): number {
  return Math.atan2(dx, dz);
}

export function dirFromYaw(yaw: number): Vec2 {
  return { x: Math.sin(yaw), z: Math.cos(yaw) };
}

/** Wraps an angle into (-PI, PI]. */
export function wrapAngle(a: number): number {
  let r = a % TAU;
  if (r <= -Math.PI) r += TAU;
  else if (r > Math.PI) r -= TAU;
  return r;
}

/** Shortest signed angular difference from `a` to `b`. */
export function angleDiff(a: number, b: number): number {
  return wrapAngle(b - a);
}

export function lerpAngle(a: number, b: number, t: number): number {
  return a + angleDiff(a, b) * t;
}

/** Frame-rate independent exponential approach: moves `current` towards `target` at `rate` per second. */
export function damp(current: number, target: number, rate: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-rate * dt));
}

export function dampAngle(current: number, target: number, rate: number, dt: number): number {
  return lerpAngle(current, target, 1 - Math.exp(-rate * dt));
}

/** True if point p lies within a cone of half-angle `halfArc` (radians) around the unit direction `dir`. */
export function inArc(ox: number, oz: number, dir: Vec2, halfArc: number, px: number, pz: number): boolean {
  const dx = px - ox;
  const dz = pz - oz;
  const l = Math.sqrt(dx * dx + dz * dz);
  if (l < 1e-6) return true;
  const cos = (dx * dir.x + dz * dir.z) / l;
  return cos >= Math.cos(halfArc);
}

/** Distance from point p to segment ab. */
export function pointSegmentDistance(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const abx = bx - ax;
  const abz = bz - az;
  const len2 = abx * abx + abz * abz;
  let t = len2 > 0 ? ((px - ax) * abx + (pz - az) * abz) / len2 : 0;
  t = clamp(t, 0, 1);
  return dist(px, pz, ax + abx * t, az + abz * t);
}
